import { useEffect, useMemo, useState } from "react";
import Top from "./Top";
import Footer from "./Footer";
import { SYMBOL_TO_SECTOR } from "../json/companies";
import { useNavigate } from "react-router-dom";
import SLNScanHeader from "./SLNScanHeader";

const API_ALL_LATEST =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/OptionChain/GetAllLatestOptionChainHistory";

/* ─── helpers ─── */
const safeNumOrNull = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
};
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
function fmtPct(v) {
  const n = toNum(v);
  if (n === null) return "—";
  return `${n > 0 ? "+" : ""}${n.toFixed(2)}%`;
}
function fmt2(n) {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n)))
    return "—";
  return Number(n).toFixed(2);
}
function chgClass(n) {
  const v = toNum(n);
  if (v === null) return "text-muted";
  return v >= 0 ? "text-success" : "text-danger";
}
function ratioClass(v) {
  const n = toNum(v);
  if (n === null) return "text-muted";
  return n >= 1 ? "text-success" : "text-danger";
}
function checkerClass(v) {
  const n = toNum(v);
  if (n === null) return "text-muted";
  return n >= 0 ? "text-success" : "text-danger";
}

function injectCSS(id, css) {
  if (document.getElementById(id)) return;
  const s = document.createElement("style");
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}

/* ─── SortIcon ─── */
function SortIcon({ col, sort }) {
  if (sort.key !== col)
    return (
      <i
        className="bi bi-arrow-down-up ms-1"
        style={{ opacity: 0.35, fontSize: 10 }}
      />
    );
  return sort.dir === "asc" ? (
    <i className="bi bi-arrow-up ms-1" style={{ fontSize: 10 }} />
  ) : (
    <i className="bi bi-arrow-down ms-1" style={{ fontSize: 10 }} />
  );
}

/* ─── SectorBars inner component ─── */
function SectorBars({ scannerRows = [], symbolToSector = {} }) {
  const [openSector, setOpenSector] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const navigate = useNavigate();
  const [showCheckersPct, setShowCheckersPct] = useState(false);

  function onSort(key) {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  }

  const sectors = useMemo(() => {
    const map = new Map();
    for (const r of scannerRows) {
      const sector = symbolToSector[r.symbol] || "OTHER";
      if (!map.has(sector))
        map.set(sector, { sector, pos: 0, neg: 0, sumPct: 0, count: 0 });
      const s = map.get(sector);
      const pct = Number(r.changePct);
      if (Number.isFinite(pct)) {
        s.sumPct += pct;
        s.count += 1;
        if (pct >= 0) s.pos += 1;
        else s.neg += 1;
      }
    }
    const arr = Array.from(map.values()).map((s) => ({
      ...s,
      avgPct: s.count ? s.sumPct / s.count : 0,
    }));
    arr.sort((a, b) => b.avgPct - a.avgPct);
    const maxAbs = Math.max(0.01, ...arr.map((x) => Math.abs(x.avgPct)));
    return arr.map((x) => ({
      ...x,
      width: Math.min(100, (Math.abs(x.avgPct) / maxAbs) * 100),
    }));
  }, [scannerRows, symbolToSector]);

  const stocksBySector = useMemo(() => {
    const map = new Map();
    for (const r of scannerRows) {
      const sector = symbolToSector[r.symbol] || "OTHER";
      if (!map.has(sector)) map.set(sector, []);
      map.get(sector).push(r);
    }
    return map;
  }, [scannerRows, symbolToSector]);

  function sortList(list) {
    if (!sort.key) return list;
    return [...list].sort((a, b) => {
      const av = a[sort.key],
        bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number")
        return sort.dir === "asc" ? av - bv : bv - av;
      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }

  const TABLE_COLS = [
    { key: "short", label: "SYMBOL" },
    { key: "volPCR", label: "VOL\nPCR" },
    { key: "optionPCR", label: "OPT\nPCR" },
    { key: "volCPR", label: "VOL\nCPR" },
    { key: "ltpChange", label: "LTP\nCHG" },
    { key: "changePct", label: "CHG\n%" },
    { key: "ltp", label: "LTP" },
    { key: "ceContract", label: "CE\nCONT%" },
    { key: "peContract", label: "PE\nCONT%" },
    { key: "volCheckerPCR", label: "PCR\nCHK" },
    { key: "volCheckerCPR", label: "VOL\nCHK CPR" },
    { key: "pressureRatio", label: "BID-ASK\nPR" },
  ];

  return (
    <div>
      {sectors.map((s) => {
        const up = s.avgPct >= 0;
        const isOpen = openSector === s.sector;
        const sectorStocks = sortList(stocksBySector.get(s.sector) || []);

        return (
          <div key={s.sector} className="ss-sector-wrap">
            {/* Header Row */}
            <button
              className={`ss-sector-header${isOpen ? " open" : ""}`}
              onClick={() => setOpenSector(isOpen ? null : s.sector)}
            >
              <div className="ss-sector-name">
                <i
                  className={`bi bi-caret-right-fill ss-chevron${isOpen ? " open" : ""}`}
                />
                {s.sector}
              </div>

              <div className="ss-pos-neg">
                <span style={{ color: "#16a34a", fontWeight: 700 }}>
                  {s.pos}↑
                </span>
                <span style={{ color: "#a0aec0", margin: "0 4px" }}>/</span>
                <span style={{ color: "#dc2626", fontWeight: 700 }}>
                  {s.neg}↓
                </span>
              </div>

              <div className="ss-bar-track">
                <div
                  className="ss-bar-fill"
                  style={{
                    width: `${s.width}%`,
                    background: up ? "#16a34a" : "#dc2626",
                  }}
                />
              </div>

              <div className={`ss-avg-pct ${up ? "clr-up" : "clr-dn"}`}>
                {s.avgPct.toFixed(2)}%
              </div>

              <div
                style={{ fontSize: 11, color: "#a0aec0", whiteSpace: "nowrap" }}
              >
                {s.count} stocks
              </div>
            </button>

            {/* Expanded Table */}
            {isOpen && (
              <div className="ss-expand-panel">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 border border-1">
                    <thead className="table-thead-bg">
                      <tr className="small text-white lh-sm">
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("short")}
                        >
                          SYMBOL <SortIcon col="short" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("volPCR")}
                        >
                          VOLUME
                          <br />
                          PCR <SortIcon col="volPCR" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("optionPCR")}
                        >
                          OPTION
                          <br />
                          PCR <SortIcon col="optionPCR" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("volCPR")}
                        >
                          VOLUME
                          <br />
                          CPR <SortIcon col="volCPR" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("ltpChange")}
                        >
                          LTP
                          <br />
                          CHANGE <SortIcon col="ltpChange" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("changePct")}
                        >
                          CHANGE
                          <br />% <SortIcon col="changePct" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("ltp")}
                        >
                          LTP <SortIcon col="ltp" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("ceContract")}
                        >
                          CE
                          <br />
                          CONT % <SortIcon col="ceContract" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("peContract")}
                        >
                          PE
                          <br />
                          CONT % <SortIcon col="peContract" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("volCheckerPCR")}
                        >
                          PCR
                          <br />
                          CHK <SortIcon col="volCheckerPCR" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("volCheckerCPR")}
                        >
                          VOL
                          <br />
                          CHK CPR <SortIcon col="volCheckerCPR" sort={sort} />
                        </th>
                        <th
                          className="text-white text-center cursor-pointer white-space-nowrap"
                          onClick={() => onSort("pressureRatio")}
                        >
                          BID-ASK
                          <br />
                          PR <SortIcon col="pressureRatio" sort={sort} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectorStocks.length === 0 ? (
                        <tr>
                          <td
                            colSpan={12}
                            className="py-5 text-center text-muted"
                          >
                            No stocks in this sector.
                          </td>
                        </tr>
                      ) : (
                        sectorStocks.map((r) => {
                          const ce = toNum(r.ceContract);
                          const pe = toNum(r.peContract);
                          const pcrChkPct =
                            toNum(r.volCheckerPCR) === null
                              ? null
                              : toNum(r.volCheckerPCR) * 100;
                          const volChkCprPct =
                            toNum(r.volCheckerCPR) === null
                              ? null
                              : toNum(r.volCheckerCPR) * 100;

                          return (
                            <tr
                              key={r.symbol}
                              className="text-black text-center fs-6"
                            >
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <div
                                    onClick={() =>
                                      navigate(
                                        `/option-chain?symbol=${encodeURIComponent(r.symbol)}&strikecount=100`,
                                      )
                                    }
                                    className="fw-semibold lh-sm fs-6 cursor-pointer text-link"
                                  >
                                    <u>{r.short}</u>
                                  </div>
                                </div>
                              </td>
                              <td>{fmt2(r.volPCR)}</td>
                              <td
                                className={chgClass(
                                  toNum(r.optionPCR) === null
                                    ? null
                                    : toNum(r.optionPCR) - 1,
                                )}
                              >
                                {fmt2(r.optionPCR)}
                              </td>
                              <td className={ratioClass(r.volCPR)}>
                                {fmt2(r.volCPR)}
                              </td>
                              <td
                                className={`fw-semibold ${chgClass(r.ltpChange)}`}
                              >
                                {fmt2(r.ltpChange)}
                              </td>
                              <td
                                className={`fw-semibold ${chgClass(r.changePct)}`}
                              >{`${fmt2(r.changePct)}%`}</td>
                              <td className="fw-semibold">{fmt2(r.ltp)}</td>
                              <td
                                className={
                                  ce === null
                                    ? "text-muted"
                                    : chgClass(ce) + " fw-semibold"
                                }
                              >
                                {ce === null ? "—" : fmtPct(ce)}
                              </td>
                              <td
                                className={
                                  pe === null
                                    ? "text-muted"
                                    : chgClass(pe) + " fw-semibold"
                                }
                              >
                                {pe === null ? "—" : fmtPct(pe)}
                              </td>
                              <td
                                className={
                                  showCheckersPct
                                    ? checkerClass(pcrChkPct)
                                    : checkerClass(r.volCheckerPCR)
                                }
                              >
                                {showCheckersPct
                                  ? fmtPct(pcrChkPct)
                                  : fmt2(r.volCheckerPCR)}
                              </td>
                              <td
                                className={
                                  showCheckersPct
                                    ? checkerClass(volChkCprPct)
                                    : checkerClass(r.volCheckerCPR)
                                }
                              >
                                {showCheckersPct
                                  ? fmtPct(volChkCprPct)
                                  : fmt2(r.volCheckerCPR)}
                              </td>
                              <td className={ratioClass(r.pressureRatio)}>
                                {fmt2(r.pressureRatio)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Toggle — matches AllCompaniesStock exactly */}
                <div className="d-flex justify-content-end align-items-center bg-dark-soft px-3 py-2">
                  <div
                    style={{
                      display: "inline-flex",
                      borderRadius: 8,
                      border: "1px solid #cbd5e0",
                      overflow: "hidden",
                    }}
                  >
                    <button
                      onClick={() => setShowCheckersPct(false)}
                      style={{
                        padding: "7px 22px",
                        fontSize: 12,
                        fontWeight: 500,
                        border: "none",
                        borderRight: "1px solid #cbd5e0",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        whiteSpace: "nowrap",
                        background: !showCheckersPct ? "#000000" : "#fff",
                        color: !showCheckersPct ? "#fff" : "#718096",
                      }}
                    >
                      Value
                    </button>
                    <button
                      onClick={() => setShowCheckersPct(true)}
                      style={{
                        padding: "7px 28px",
                        fontSize: 12,
                        fontWeight: 500,
                        border: "none",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        whiteSpace: "nowrap",
                        background: showCheckersPct ? "#000000" : "#fff",
                        color: showCheckersPct ? "#fff" : "#718096",
                      }}
                    >
                      Percentage %
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main Page ─── */
function SectorStrength() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    injectCSS("ss-css", CSS);
  }, []);

  async function loadAll() {
    try {
      setErr("");
      setLoading(true);
      setRows([]);
      const token = localStorage.getItem("SLNToken");
      if (!token) throw new Error("Token not found.");
      const res = await fetch(`${API_ALL_LATEST}?page=1&limit=200`, {
        method: "GET",
        headers: { Authorization: token, "Content-Type": "application/json" },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.s === "error")
        throw new Error(json?.message || `Request failed (${res.status})`);
      const list = Array.isArray(json?.rows) ? json.rows : [];
      setRows(
        list
          .map((r) => ({
            symbol: String(r.symbol || ""),
            short:
              String(r.short || "") ||
              String(r.symbol || "")
                .replace("NSE:", "")
                .replace("-EQ", ""),
            volPCR: safeNumOrNull(r.volPCR),
            volCPR: safeNumOrNull(r.volCPR),
            optionPCR: safeNumOrNull(r.optionPCR),
            ltp: safeNumOrNull(r.ltp),
            ltpChange: safeNumOrNull(r.ltpChange),
            changePct: safeNumOrNull(r.changePct),
            ceContract: safeNumOrNull(r.ceContract),
            peContract: safeNumOrNull(r.peContract),
            volCheckerPCR: safeNumOrNull(r.volCheckerPCR),
            volCheckerCPR: safeNumOrNull(r.volCheckerCPR),
            pcrChecker: safeNumOrNull(r.pcrChecker),
            putOiChange: safeNumOrNull(r.putOiChange),
            callOiChange: safeNumOrNull(r.callOiChange),
            volume: safeNumOrNull(r.volume),
            pressureRatio: safeNumOrNull(r.pressureRatio),
          }))
          .filter((x) => x.symbol && x.short),
      );
    } catch (e) {
      setErr(e?.message || "Something went wrong");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.short.toLowerCase().includes(q) || r.symbol.toLowerCase().includes(q),
    );
  }, [rows, search]);

  useEffect(() => {
    loadAll();
  }, []);

  return (
    <>
      <Top />
      <SLNScanHeader page="sector" />

      <div className="ss-root py-5">
        <div className="container">
          {err && (
            <div
              style={{
                background: "#fff5f5",
                border: "1px solid #fed7d7",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 16,
                color: "#c53030",
                fontSize: 13,
              }}
            >
              <i className="bi bi-exclamation-triangle-fill me-2" />
              {err}
            </div>
          )}

          {loading ? (
            <div>
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="skeleton mb-2"
                  style={{ height: 52, borderRadius: 10 }}
                />
              ))}
            </div>
          ) : (
            <SectorBars
              scannerRows={filtered}
              symbolToSector={SYMBOL_TO_SECTOR}
            />
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}

export default SectorStrength;
